/**
 * The status chip for a template, and the one mapping from a template's status
 * to a badge tone.
 *
 * Two of the four states in the fixed `TemplateStatus` enum cannot be reached by
 * an email template — `pending` and `rejected` are Meta's, for WhatsApp in slice
 * 7 — and the chip renders them anyway rather than falling back to a default,
 * because a template that somehow arrived in one of them is exactly the case
 * where a blank chip would hide the problem.
 *
 * ## WHY THE TONE IS DECIDED HERE AND NOT IN THE DISPLAY TABLES
 *
 * Slice 66 adopted the Blueprint's template status mapping: draft `neutral`,
 * pending `warn`, approved `good`, rejected `bad`. Until then "With Meta" wore
 * `info`, the colour of a notice — and a template waiting on Meta's review is
 * not a notice, it is a thing that cannot be sent yet, which is what `warn`
 * means everywhere else on these screens. The email and WhatsApp editors drew
 * their badges from two tables (`TEMPLATE_STATUS_DISPLAY`,
 * `WA_STATUS_DISPLAY`), each with its own `tone` field, and the two could
 * disagree about the same status. `TEMPLATE_STATUS_TONE` is the one place now;
 * both WhatsApp screens read it too.
 *
 * The `tone` fields those two `lib/` tables used to carry were deleted in the
 * same slice, so the tables are words only and this record cannot be
 * contradicted by a second one that still compiles. The hole it leaves: a new
 * screen that draws a template status without going through this file gets no
 * colour from the tables at all and has to come here for it — which is the
 * point, but nothing but this comment says so.
 */
import type { TemplateStatus } from '@wend/shared';
import { Badge, type BadgeTone } from '@wend/ui';

import { TEMPLATE_STATUS_DISPLAY } from '../lib/template-display';

/** A total record, so a fifth status does not compile until it has a tone. */
export const TEMPLATE_STATUS_TONE: Readonly<Record<TemplateStatus, BadgeTone>> = {
  draft: 'neutral',
  pending: 'warn',
  approved: 'good',
  rejected: 'bad',
};

export interface TemplateStatusBadgeProps {
  status: TemplateStatus;
}

export function TemplateStatusBadge({ status }: TemplateStatusBadgeProps) {
  return <Badge tone={TEMPLATE_STATUS_TONE[status]}>{TEMPLATE_STATUS_DISPLAY[status].label}</Badge>;
}
